import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { PageHero, Reveal, SectionTitle, Icon, Kicker } from '../components/common';
import { img, givingTiers, givingWays } from '../mock/mock';
import { useToast } from '../hooks/use-toast';

const amounts = ['50', '100', '250', '1000'];

const WaysToGive = () => {
  const { toast } = useToast();
  const [selected, setSelected] = useState('250');
  const [custom, setCustom] = useState('');

  const donate = () => {
    const amt = custom || selected;
    toast({ title: 'Thank you for your generosity!', description: `Your $${amt} gift is a visual demo for now — payment processing can be connected later.` });
  };

  return (
    <div>
      <PageHero image={img.volunteersShovels} kicker="Ways To Give" title="Plant the future, today" sub="Every gift helps cultivate gardens, protect native habitat and open the doors to learning for all." />

      {/* Ways grid */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="Get Involved" title="Four meaningful ways to support the garden" /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {givingWays.map((w, i) => (
              <Reveal key={w.title} delay={i * 100}>
                <div className="h-full p-8 rounded-2xl bg-white border border-sand shadow-lg hover:-translate-y-1.5 transition-transform duration-300 text-center">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-forest/8 flex items-center justify-center mb-5">
                    <Icon name={w.icon} className="w-7 h-7 text-forest" />
                  </div>
                  <h3 className="font-display text-xl text-forest">{w.title}</h3>
                  <p className="text-ink/65 text-sm mt-3 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Donation widget */}
      <section className="py-24 bg-forest text-cream">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionTitle light kicker="Make a Gift" title="Your generosity grows here" sub="Choose an amount to see how your gift helps the garden flourish." />
            <img src={img.hydrangeaPurple} alt="Blooms" className="rounded-2xl w-full h-56 object-cover mt-8 shadow-xl" />
          </Reveal>
          <Reveal delay={150}>
            <div className="bg-cream text-ink rounded-2xl p-8 shadow-2xl">
              <h3 className="font-display text-2xl text-forest mb-6">Give a one-time gift</h3>
              <div className="grid grid-cols-4 gap-3">
                {amounts.map((a) => (
                  <button key={a} onClick={() => { setSelected(a); setCustom(''); }} className={`py-3 rounded-lg font-600 text-sm border-2 transition-all ${selected === a && !custom ? 'border-gold bg-gold text-white' : 'border-sand bg-white text-forest hover:border-gold'}`}>${a}</button>
                ))}
              </div>
              <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Custom amount" className="w-full mt-4 px-4 py-3 rounded-lg border-2 border-sand focus:border-gold focus:outline-none text-sm" />
              <button onClick={donate} className="w-full mt-5 py-4 rounded-full bg-gold text-white font-600 hover:bg-gold-light transition-colors flex items-center justify-center gap-2">
                Donate ${custom || selected} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-ink/50 text-center mt-4">Secure giving. The Conservancy is a 501(c)(3) nonprofit; gifts are tax-deductible.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="Founding Circles" title="Become a founding supporter" sub="Founding gifts are recognized for generations. Choose the circle that's right for you." /></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-14 items-stretch">
            {givingTiers.map((t, i) => (
              <Reveal key={t.name} delay={i * 120}>
                <div className={`h-full rounded-2xl p-8 shadow-lg border-2 flex flex-col ${t.featured ? 'border-gold bg-forest text-cream scale-[1.03]' : 'border-sand bg-white'}`}>
                  {t.featured && <span className="self-start px-3 py-1 rounded-full bg-gold text-white text-xs font-600 mb-4">Most Popular</span>}
                  <h3 className={`font-display text-2xl ${t.featured ? 'text-white' : 'text-forest'}`}>{t.name}</h3>
                  <div className={`font-display text-4xl mt-3 ${t.featured ? 'text-gold-light' : 'text-forest'}`}>{t.amount}</div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {t.perks.map((p) => (
                      <li key={p} className={`flex gap-3 text-sm ${t.featured ? 'text-cream/85' : 'text-ink/70'}`}>
                        <Check className={`w-4 h-4 shrink-0 mt-0.5 ${t.featured ? 'text-gold-light' : 'text-forest-light'}`} />{p}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => toast({ title: `${t.name} circle selected`, description: 'This is a visual demo — checkout can be connected later.' })} className={`mt-8 py-3.5 rounded-full font-600 text-sm transition-colors ${t.featured ? 'bg-gold text-white hover:bg-gold-light' : 'border-2 border-forest text-forest hover:bg-forest hover:text-cream'}`}>Join as {t.name}</button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WaysToGive;
